import type { StaffRequestThread } from '@/services/request-thread.service';

/**
 * What the customer asked for — in BOTH of the forms that exist.
 *
 * ── WHY TWO BLOCKS ─────────────────────────────────────────────────────────
 * `original` is the archival opening statement and is never rewritten.
 * `latest` is the newest customer-meaning entry, and on INS-00201 it says
 * something different: the opening line is "Ok we will remove some items" while
 * the operative ask, added a day later, is "we can change". Showing only the
 * first is how a reader ends up acting on a five-day-old sentence — the exact
 * confusion the forensic audit found. Showing only the latest would lose the
 * record. So: both, labelled, never merged.
 *
 * When nothing was ever added the two are equal and ONE block is rendered
 * instead — two identical blocks would imply a change that did not happen. That
 * is a rendering decision only; neither string is altered.
 *
 * ── VERBATIM ────────────────────────────────────────────────────────────────
 * No trim, no normalisation, no collapsing of whitespace. "we can change " has a
 * trailing space because that is what the customer sent. `whitespace-pre-wrap`
 * preserves the author's own line breaks.
 */

function Block({
  label,
  body,
  at,
  testId,
}: {
  label: string;
  body: string;
  at?: string;
  testId: string;
}) {
  return (
    <div className="rounded-lg border border-border bg-card px-4 py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {label}
        </p>
        {at && <p className="text-xs text-muted-foreground">{at}</p>}
      </div>
      <p
        data-testid={testId}
        className="mt-1.5 whitespace-pre-wrap text-sm leading-relaxed text-foreground"
      >
        {body}
      </p>
    </div>
  );
}

export function RequestBlocks({ thread }: { thread: StaffRequestThread }) {
  const { original, latest } = thread.request;
  const opened = new Date(thread.createdAt).toLocaleString();
  // Compared, never rewritten — an equality check is not a mutation.
  const amended = latest !== null && latest !== original;

  return (
    <section aria-label="The customer’s request" className="space-y-3">
      <Block
        label={amended ? 'Original request' : 'Customer request'}
        body={original}
        at={`Requested ${opened}`}
        testId="thread-original-request"
      />
      {amended && (
        <Block label="Latest customer request" body={latest} testId="thread-latest-request" />
      )}
    </section>
  );
}
